import type { TFunction } from 'i18next'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

/**
 * The backend seeds "default" values (cost categories, inventory categories,
 * roles, units, breeds) with Thai names. Users cannot rename or reuse them, so
 * the Thai name is a reliable identifier. Each exact (trimmed) name maps to an
 * i18n key in the `common` namespace; anything else (user-created values) is
 * returned unchanged via the `t(key, rawName)` default.
 *
 * Translate ONLY at display time. Keep raw values for comparisons, chart
 * series ids/colors, select option values and RoleName checks.
 */

export enum CostCategoryDefault {
  Food = 'ค่าอาหาร',
  Medicine = 'ค่าเวชภัณฑ์',
  Larvae = 'ค่าลูกพันธุ์',
  ShrimpLarvae = 'ค่าลูกกุ้ง',
  FishLarvae = 'ค่าลูกปลา',
  Labor = 'ค่าแรงงาน',
  Energy = 'ค่าพลังงานและเชื้อเพลิง',
  PondPrep = 'ค่าเตรียมบ่อ',
  Maintenance = 'ค่าซ่อมบำรุง',
  Misc = 'ค่าใช้จ่ายเบ็ดเตล็ด',
  Depreciation = 'ค่าเสื่อมสภาพ',
  Unspecified = 'ไม่ระบุ',
}

export enum InventoryCategoryDefault {
  Food = 'อาหาร',
  Medicine = 'เวชภัณฑ์',
  NaturalFood = 'อาหารธรรมชาติ',
}

export enum DefaultRole {
  PrimaryOwner = 'เจ้าของฟาร์ม',
  SecondaryOwner = 'รองเจ้าของฟาร์ม',
  FarmManager = 'ผู้จัดการฟาร์ม',
  PondManager = 'ผู้จัดการบ่อ',
}

export enum BreedProductDefault {
  Nursery = 'อนุบาล',
}

const COST_CATEGORY_KEYS: Record<CostCategoryDefault, string> = {
  [CostCategoryDefault.Food]: 'default.cost.food',
  [CostCategoryDefault.Medicine]: 'default.cost.medicine',
  [CostCategoryDefault.Larvae]: 'default.cost.larvae',
  [CostCategoryDefault.ShrimpLarvae]: 'default.cost.shrimp_larvae',
  [CostCategoryDefault.FishLarvae]: 'default.cost.fish_larvae',
  [CostCategoryDefault.Labor]: 'default.cost.labor',
  [CostCategoryDefault.Energy]: 'default.cost.energy',
  [CostCategoryDefault.PondPrep]: 'default.cost.pond_prep',
  [CostCategoryDefault.Maintenance]: 'default.cost.maintenance',
  [CostCategoryDefault.Misc]: 'default.cost.misc',
  [CostCategoryDefault.Depreciation]: 'default.cost.depreciation',
  [CostCategoryDefault.Unspecified]: 'default.cost.unspecified',
}

const INVENTORY_CATEGORY_KEYS: Record<InventoryCategoryDefault, string> = {
  [InventoryCategoryDefault.Food]: 'default.inventory.food',
  [InventoryCategoryDefault.Medicine]: 'default.inventory.medicine',
  [InventoryCategoryDefault.NaturalFood]: 'default.inventory.natural_food',
}

const ROLE_KEYS: Record<DefaultRole, string> = {
  [DefaultRole.PrimaryOwner]: 'default.role.primary_owner',
  [DefaultRole.SecondaryOwner]: 'default.role.secondary_owner',
  [DefaultRole.FarmManager]: 'default.role.farm_manager',
  [DefaultRole.PondManager]: 'default.role.pond_manager',
}

// Seeded Unit names (V0.2__init_data.sql) plus the two abbreviations the
// backend commonly sends ("กก.", "ก.").
const UNIT_KEYS: Record<string, string> = {
  กิโลกรัม: 'default.unit.kg',
  'กก.': 'default.unit.kg_short',
  กรัม: 'default.unit.g',
  'ก.': 'default.unit.g_short',
  ตัน: 'default.unit.ton',
  ตัว: 'default.unit.animal',
  ลิตร: 'default.unit.liter',
  เดือน: 'default.unit.month',
  วัน: 'default.unit.day',
  ปี: 'default.unit.year',
  กิโลวัตต์: 'default.unit.kw',
  กระสอบ: 'default.unit.sack',
  อัน: 'default.unit.piece',
  ท่อน: 'default.unit.log',
  รอบ: 'default.unit.round',
  ซอง: 'default.unit.sachet',
  เส้น: 'default.unit.strand',
  เที่ยว: 'default.unit.trip',
  ถัง: 'default.unit.bucket',
  ชิ้น: 'default.unit.item',
}

// Seeded Breed ids (stable) and names.
const BREED_KEYS_BY_ID: Record<number, string> = {
  1: 'default.breed.white_shrimp',
  2: 'default.breed.black_shrimp',
  3: 'default.breed.giant_prawn',
  4: 'default.breed.tilapia',
  5: 'default.breed.red_tilapia',
  6: 'default.breed.catfish',
  7: 'default.breed.sea_bass',
}

const BREED_KEYS_BY_NAME: Record<string, string> = {
  กุ้งขาว: BREED_KEYS_BY_ID[1],
  กุ้งดำ: BREED_KEYS_BY_ID[2],
  กุ้งก้ามกราม: BREED_KEYS_BY_ID[3],
  ปลานิล: BREED_KEYS_BY_ID[4],
  ปลาทับทิม: BREED_KEYS_BY_ID[5],
  ปลาดุก: BREED_KEYS_BY_ID[6],
  ปลากะพง: BREED_KEYS_BY_ID[7],
}

const BREED_PRODUCT_KEYS: Record<BreedProductDefault, string> = {
  [BreedProductDefault.Nursery]: 'default.breed_product.nursery',
}

const lookup = (map: Record<string, string>, raw?: string | null): string | undefined => {
  const name = raw?.trim()
  return name && Object.prototype.hasOwnProperty.call(map, name) ? map[name] : undefined
}

type Translate = TFunction | ((key: string, defaultValue?: string) => string)

const translate = (t: Translate, key: string | undefined, raw?: string | null): string => {
  const fallback = raw ?? ''
  if (!key) return fallback
  return (t as (k: string, d?: string) => string)(key, fallback)
}

export const costCategoryLabel = (t: Translate, name?: string | null) =>
  translate(t, lookup(COST_CATEGORY_KEYS, name), name)

/** `isDefault === false` means a user-created category: never translate it. */
export const inventoryCategoryLabel = (
  t: Translate,
  name?: string | null,
  isDefault?: boolean | null
) => translate(t, isDefault === false ? undefined : lookup(INVENTORY_CATEGORY_KEYS, name), name)

/** Roles are translated only for default/preset roles when `roleType` is known. */
export const roleLabel = (t: Translate, name?: string | null, roleType?: string | null) => {
  const isBuiltIn = !roleType || roleType === 'default' || roleType === 'preset'
  return translate(t, isBuiltIn ? lookup(ROLE_KEYS, name) : undefined, name)
}

export const unitLabel = (t: Translate, unit?: string | null) =>
  translate(t, lookup(UNIT_KEYS, unit), unit)

export const breedLabel = (t: Translate, name?: string | null, id?: number | null) =>
  translate(
    t,
    (id != null ? BREED_KEYS_BY_ID[id] : undefined) ?? lookup(BREED_KEYS_BY_NAME, name),
    name
  )

export const breedProductLabel = (t: Translate, name?: string | null) =>
  translate(t, lookup(BREED_PRODUCT_KEYS, name), name)

/** Hook bound to the `common` namespace where the default-value keys live. */
export const useDefaultValueLabel = () => {
  const { t } = useTranslation('common')
  return useMemo(
    () => ({
      costCategory: (name?: string | null) => costCategoryLabel(t, name),
      inventoryCategory: (name?: string | null, isDefault?: boolean | null) =>
        inventoryCategoryLabel(t, name, isDefault),
      role: (name?: string | null, roleType?: string | null) => roleLabel(t, name, roleType),
      unit: (unit?: string | null) => unitLabel(t, unit),
      breed: (name?: string | null, id?: number | null) => breedLabel(t, name, id),
      breedProduct: (name?: string | null) => breedProductLabel(t, name),
    }),
    [t]
  )
}
